// pages/NotesPlain.jsx
// [Notes Plain] Catatan TANPA indeks RAG — halaman terpisah dari Notes (RAG).
// Tujuan: tempat kerja draft & impor dokumen (PDF/Word/Excel → Markdown).
// Setelah rapi, catatan bisa di-PROMOTE (Salin / Pindah) ke Notes versi RAG
// via POST /api/notes-plain/:id/promote — baru pada saat itu diindeks ke KB.
//
// Desain: Tailwind, 2 panel di desktop (daftar kiri + editor/preview kanan),
// full-screen panel di mobile. Renderer markdown DISALIN dari Notes.jsx agar
// hasil render identik ketika catatan dipindah ke Notes RAG.
// Permission: 'notes_plain' (dicek juga di backend).
import { useState, useEffect, useCallback, useRef } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import useIsMobile from '../hooks/useIsMobile';
import { downloadNotesZip } from '../utils/notesExport';
import ImportNotesZipModal from '../components/ImportNotesZipModal';
// Helper lampiran gambar dipakai bersama dengan AI Workspace — lihat
// utils/imageAttach.js (pure, tanpa React, bebas circular import).
import { isImageFile, humanBytes, imageFileToDataUri, IMG_MAX_BYTES } from '../utils/imageAttach';
import {
  NotebookText, Plus, FileUp, Search, Save, X, Eye, Pencil, Trash2,
  ArrowLeft, RefreshCw, Lock, Users as UsersIcon, Sparkles,
  Copy as CopyIcon, CornerUpRight, AlertTriangle, CheckCircle2,
  ChevronLeft, ChevronRight, Image as ImageIcon,
  Download, CheckSquare,
} from 'lucide-react';

// ── useApi — axios instance dari AuthContext (token otomatis via interceptor) ─
function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = method === 'get' || method === 'delete'
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, []);
}

// ── Markdown ringan (tanpa dependency) — render heading, bold, italic, code,
// list, blockquote, hr, link, tabel. Aman: pakai elemen React, bukan innerHTML.

// Palet code block — tema terang, senada dengan sisa dashboard.
const CB = {
  bg: '#f8fafc', border: '#e2e8f0', label: '#94a3b8', text: '#334155',
  cmd: '#059669', flag: '#0284c7', str: '#be123c', comment: '#94a3b8',
};

// Bahasa yang diwarnai. Selain ini dirender polos — lebih baik tanpa warna
// daripada warna yang salah.
const SHELLS = new Set(['bash', 'sh', 'shell', 'zsh', 'console', 'terminal']);

// Highlighter shell seadanya. Sengaja TIDAK memakai innerHTML: setiap token
// jadi elemen React, jadi isi catatan tidak bisa menyuntikkan markup.
// Cakupannya sempit dan itu disengaja — komentar, string, flag, dan kata di
// posisi perintah. Selebihnya dibiarkan polos.
function highlightShell(code) {
  // Token: string kutip (3 gaya) | komentar | spasi | sisanya
  // Operator dipisah jadi token sendiri, supaya kata setelah `;` atau `|`
  // ikut dikenali sebagai posisi perintah (mis. `do` pada `...; do`).
  const RE = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`|#[^\n]*|\s+|&&|\|\||[;|()]|[^\s;|()&]+)/g;
  const out = [];
  let k = 0;

  code.split('\n').forEach((line, li) => {
    if (li > 0) out.push('\n');
    // Kata pertama sebuah perintah: awal baris, atau tepat setelah | && || ; (
    let expectCmd = true;
    const toks = line.match(RE) || [];
    for (const tok of toks) {
      const c = tok[0];
      if (/^\s+$/.test(tok)) { out.push(tok); continue; }

      let color = null;
      if (c === '#') color = CB.comment;
      else if (c === '"' || c === "'" || c === '`') { color = CB.str; expectCmd = false; }
      else if (/^-{1,2}[A-Za-z0-9]/.test(tok)) { color = CB.flag; expectCmd = false; }
      else if (/^(\||&&|\|\||;|\()$/.test(tok)) { expectCmd = true; }
      else if (expectCmd) { color = CB.cmd; expectCmd = false; }
      else expectCmd = false;

      out.push(color
        ? <span key={`h${k++}`} style={{ color, ...(c === '#' ? { fontStyle: 'italic' } : null) }}>{tok}</span>
        : tok);
    }
  });
  return out;
}

function CodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shell = lang && SHELLS.has(String(lang).toLowerCase());

  return (
    <div
      style={{
        position: 'relative', margin: '8px 0',
        background: CB.bg, border: `1px solid ${CB.border}`, borderRadius: 8,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {lang && (
        <div style={{
          fontSize: 10.5, color: CB.label, fontFamily: 'monospace',
          userSelect: 'none', padding: '8px 14px 0',
        }}>
          {lang}
        </div>
      )}
      <button
        onClick={copy}
        style={{
          position: 'absolute', top: 6, right: 6,
          background: copied ? '#16a34a' : '#ffffff',
          border: `1px solid ${copied ? '#16a34a' : CB.border}`,
          borderRadius: 6, padding: '3px 9px',
          color: copied ? '#fff' : '#64748b',
          fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
          transition: 'all 0.15s', opacity: hovered || copied ? 1 : 0,
        }}
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
      <pre style={{
        background: 'transparent', color: CB.text, borderRadius: 0,
        padding: `${lang ? '4px' : '12px'} 14px 12px`,
        fontSize: 12.5, fontFamily: 'monospace', overflowX: 'auto',
        lineHeight: 1.5, margin: 0,
      }}>
        {shell ? highlightShell(code) : code}
      </pre>
    </div>
  );
}

function mdInline(text, keyPrefix, hl) {
  const nodes = [];
  let rest = text;
  let i = 0;
  // Urutan alternasi penting: pola gambar (!\[...\](...)) diletakkan SEBELUM
  // pola link biasa, dan diberi tanda '!' eksplisit supaya tidak ketangkap
  // sebagai link teks "nama_file.png" yang salah.
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)|(!\[[^\]]*\]\([^)]+\))|(\[[^\]]+\]\([^)]+\))/;

  // Pecah teks biasa dengan highlight marker. Mendukung newline literal di
  // dalam teks (mis. base64 data-URI yang ter-wrap multi-baris) dengan
  // mengonversinya jadi elemen <br/> alih-alih dibiarkan sebagai whitespace.
  const pushText = (t, k) => {
    if (!t) return;
    const lines = t.split('\n');
    lines.forEach((ln, li) => {
      if (!hl) {
        if (ln) nodes.push(ln);
      } else {
        const lower = ln.toLowerCase();
        const nl    = hl.toLowerCase();
        let pos = 0, ki = 0;
        while (pos < ln.length) {
          const idx = lower.indexOf(nl, pos);
          if (idx === -1) { nodes.push(ln.slice(pos)); break; }
          if (idx > pos) nodes.push(ln.slice(pos, idx));
          nodes.push(
            <mark key={`${k}-h${li}-${ki++}`} data-pvhl="true"
              style={{ background: '#fef08a', color: '#0f172a', borderRadius: 2, padding: '0 1px' }}>
              {ln.slice(idx, idx + hl.length)}
            </mark>
          );
          pos = idx + hl.length;
        }
      }
      if (li < lines.length - 1) nodes.push(<br key={`${k}-br${li}`} />);
    });
  };

  while (rest.length) {
    const m = rest.match(re);
    if (!m) { pushText(rest, `${keyPrefix}-${i++}`); break; }
    if (m.index > 0) pushText(rest.slice(0, m.index), `${keyPrefix}-${i++}`);
    const tok = m[0];
    const k = `${keyPrefix}-${i++}`;
    if (tok.startsWith('`')) {
      nodes.push(<code key={k} style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.92em', color: '#be123c' }}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={k} style={{ fontWeight: 700, color: '#0f172a' }}>{mdInline(tok.slice(2, -2), k, hl)}</strong>);
    } else if (tok.startsWith('!')) {
      const im = tok.match(/!\[([^\]]*)\]\(([^)]+)\)/);
      if (im) nodes.push(
        <a key={k} href={im[2]} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
          <img src={im[2]} alt={im[1] || ''} style={{ maxWidth: 320, maxHeight: 320, width: 'auto', height: 'auto', borderRadius: 8, display: 'block', margin: '6px 0', border: '1px solid #e2e8f0', cursor: 'zoom-in' }} />
        </a>
      );
      else pushText(tok, k);
    } else if (tok.startsWith('*')) {
      nodes.push(<em key={k}>{mdInline(tok.slice(1, -1), k, hl)}</em>);
    } else if (tok.startsWith('_')) {
      nodes.push(<em key={k}>{mdInline(tok.slice(1, -1), k, hl)}</em>);
    } else {
      const lm = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (lm) nodes.push(<a key={k} href={lm[2]} target="_blank" rel="noopener noreferrer" style={{ color: 'rgb(var(--brand-600))', textDecoration: 'underline' }}>{lm[1]}</a>);
      else pushText(tok, k);
    }
    rest = rest.slice(m.index + tok.length);
  }
  return nodes;
}
function MarkdownView({ source, highlight }) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let bi = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block ```
    if (/^```/.test(line.trim())) {
      const lang = line.trim().replace(/^```/, '').trim().toLowerCase();
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) { buf.push(lines[i]); i++; }
      i++; // skip closing ```
      const rawContent = buf.join('\n');
      // Jika lang = svg/html atau isi dimulai dengan <svg / <div / <html → render sebagai HTML
      const isHtmlContent = ['svg', 'html'].includes(lang) ||
        /^\s*<(svg|div|html|figure)\b/i.test(rawContent.trimStart());
      if (isHtmlContent) {
        blocks.push(
          <div key={`b${bi++}`} style={{ margin: '8px 0', overflowX: 'auto', maxWidth: '100%' }}>
            <style>{`
              .md-html-block svg { max-width: 720px !important; width: 100% !important; height: auto !important; }
              .md-html-block img { max-width: 100%; height: auto; }
            `}</style>
            <div className="md-html-block" dangerouslySetInnerHTML={{ __html: rawContent }} />
          </div>
        );
      } else {
        blocks.push(
          <CodeBlock key={`b${bi++}`} lang={lang} code={rawContent} />
        );
      }
      continue;
    }

    // Raw HTML / SVG block — render dengan dangerouslySetInnerHTML
    if (/^<[a-zA-Z!\/]/.test(line.trim())) {
      const tagMatch = line.trim().match(/^<([a-zA-Z][a-zA-Z0-9]*)/);
      const openTag  = tagMatch ? tagMatch[1].toLowerCase() : null;
      const buf = [line];
      i++;
      // Untuk tag blok (div, svg, dll): kumpulkan sampai closing tag — abaikan blank line di dalam
      const blockTags = ['div', 'svg', 'figure', 'section', 'article', 'details', 'summary'];
      if (openTag && blockTags.includes(openTag)) {
        const closeTag = `</${openTag}`;
        while (i < lines.length) {
          buf.push(lines[i]);
          if (lines[i].toLowerCase().includes(closeTag)) { i++; break; }
          i++;
        }
      } else {
        // Tag pendek: hentikan di baris kosong
        while (i < lines.length && lines[i].trim() !== '') { buf.push(lines[i]); i++; }
      }
      blocks.push(
        <div key={`b${bi++}`} style={{ margin: '8px 0', overflowX: 'auto', maxWidth: '100%' }}>
          <div className="md-html-block" dangerouslySetInnerHTML={{ __html: buf.join('\n') }} />
        </div>
      );
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line.trim())) {
      blocks.push(<hr key={`b${bi++}`} style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '14px 0' }} />);
      i++; continue;
    }

    // Heading
    const hm = line.match(/^(#{1,6})\s+(.*)$/);
    if (hm) {
      const level = hm[1].length;
      const sizes = { 1: 20, 2: 17, 3: 15, 4: 14, 5: 13, 6: 12.5 };
      const Tag = `h${Math.min(level, 6)}`;
      blocks.push(
        <Tag key={`b${bi++}`} style={{ fontSize: sizes[level] || 14, fontWeight: 700, color: '#0f172a', margin: level <= 2 ? '16px 0 8px' : '12px 0 6px', lineHeight: 1.3 }}>
          {mdInline(hm[2], `h${bi}`, highlight)}
        </Tag>
      );
      i++; continue;
    }

    // Table (GFM pipe table)
    const isDivider = (s) => /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/.test(s) && s.includes('-');
    if (line.includes('|') && i + 1 < lines.length && isDivider(lines[i + 1])) {
      const splitRow = (s) => s.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
      const headers  = splitRow(line);
      const aligns   = splitRow(lines[i + 1]).map(d => {
        const t = d.trim();
        if (t.startsWith(':') && t.endsWith(':')) return 'center';
        if (t.endsWith(':')) return 'right';
        return 'left';
      });
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '' && !isDivider(lines[i])) {
        rows.push(splitRow(lines[i])); i++;
      }
      blocks.push(
        <div key={`b${bi++}`} style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5, border: '1px solid #e2e8f0' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {headers.map((h, ci) => (
                  <th key={ci} style={{ textAlign: aligns[ci] || 'left', padding: '7px 12px', fontWeight: 700, color: '#0f172a', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                    {mdInline(h, `th${bi}-${ci}`, highlight)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} style={{ background: ri % 2 ? '#f8fafc' : '#fff' }}>
                  {headers.map((_, ci) => (
                    <td key={ci} style={{ textAlign: aligns[ci] || 'left', padding: '6px 12px', color: '#334155', borderBottom: '1px solid #f1f5f9' }}>
                      {mdInline((r[ci] || ''), `td${bi}-${ri}-${ci}`, highlight)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, '')); i++; }
      blocks.push(
        <blockquote key={`b${bi++}`} style={{ borderLeft: '3px solid #cbd5e1', paddingLeft: 12, margin: '8px 0', color: '#475569', fontStyle: 'italic' }}>
          {buf.map((b, j) => <div key={j}>{mdInline(b, `bq${bi}-${j}`, highlight)}</div>)}
        </blockquote>
      );
      continue;
    }

    // Unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, '')); i++;
      }
      blocks.push(
        <ul key={`b${bi++}`} style={{ margin: '8px 0', paddingLeft: 22, lineHeight: 1.6 }}>
          {items.map((it, j) => <li key={j} style={{ marginBottom: 3 }}>{mdInline(it, `ul${bi}-${j}`, highlight)}</li>)}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, '')); i++;
      }
      blocks.push(
        <ol key={`b${bi++}`} style={{ margin: '8px 0', paddingLeft: 24, lineHeight: 1.6 }}>
          {items.map((it, j) => <li key={j} style={{ marginBottom: 3 }}>{mdInline(it, `ol${bi}-${j}`, highlight)}</li>)}
        </ol>
      );
      continue;
    }

    // Baris kosong → skip
    if (line.trim() === '') { i++; continue; }

    // Paragraf. Baris kosong biasanya mengakhiri paragraf, KECUALI kita sedang
    // di tengah pola gambar/link markdown yang belum tertutup (mis. base64
    // data-URI yang sangat panjang dan kebetulan ter-paste dengan baris kosong
    // menyelip di tengahnya) — dalam kasus itu baris kosong tetap dianggap
    // bagian dari paragraf sampai tanda kurung tutup ')' ditemukan.
    const hasUnclosedLinkImage = (t) => {
      const idx = t.lastIndexOf('](');
      return idx !== -1 && t.indexOf(')', idx + 2) === -1;
    };
    const para = [];
    while (i < lines.length) {
      const cur = lines[i];
      const isBlank = cur.trim() === '';
      const insideUnclosed = para.length > 0 && hasUnclosedLinkImage(para.join('\n'));
      if (isBlank && !insideUnclosed) break;
      if (!isBlank &&
          (/^```/.test(cur.trim()) || /^#{1,6}\s/.test(cur) || /^>\s?/.test(cur) ||
           /^\s*[-*+]\s+/.test(cur) || /^\s*\d+\.\s+/.test(cur) ||
           /^(-{3,}|\*{3,}|_{3,})\s*$/.test(cur.trim()) ||
           (cur.includes('|') && i + 1 < lines.length && isDivider(lines[i + 1])))) {
        break;
      }
      para.push(cur); i++;
    }
    blocks.push(
      <p key={`b${bi++}`} style={{ margin: '6px 0', lineHeight: 1.6, color: '#334155', wordBreak: 'break-word' }}>
        {mdInline(para.join('\n'), `p${bi}`, highlight)}
      </p>
    );
  }

  return <div style={{ fontSize: 13 }}>{blocks}</div>;
}

// ── UI kecil (Tailwind) ───────────────────────────────────────────────────────
const BTN_TONE = {
  default: 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 active:bg-slate-100',
  primary: 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700',
  emerald: 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  amber:   'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100',
  danger:  'border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100',
};
function Btn({ children, onClick, tone = 'default', size = 'md', disabled, title, className = '' }) {
  return (
    <button onClick={disabled ? undefined : onClick} title={title} disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 border rounded-xl font-semibold transition-colors
        ${size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm min-h-[40px]'}
        ${BTN_TONE[tone] || BTN_TONE.default} disabled:opacity-40 disabled:cursor-not-allowed ${className}`}>
      {children}
    </button>
  );
}

function ScopeBadge({ scope, id }) {
  const {
    t
  } = useLang();

  return scope === 'common'
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-600 border border-blue-200"><UsersIcon style={{ width: 11, height: 11 }} />{t('notesPlain.shared')}</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500 border border-slate-200"><Lock style={{ width: 11, height: 11 }} />{t('notesPlain.private')}</span>;
}

function fmtDt(s, locale) {
  if (!s) return '—';
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
  return isNaN(d) ? s : d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

// ── DocReviewModal — tinjau & edit markdown hasil konversi dokumen ───────────
// Diadaptasi dari PdfReviewModal (Notes.jsx). Mobile-friendly (full-screen).
function DocReviewModal({ id, result, onClose, onInsertText, onInsertImage }) {
  const {
    t
  } = useLang();

  const [md, setMd]   = useState(result?.markdown || '');
  const [tab, setTab] = useState('edit'); // 'edit' | 'preview'
  const [insertedImgs, setInsertedImgs] = useState({});
  const overlayRef = useRef(null);
  const meta   = result?.meta || {};
  const images = Array.isArray(result?.images) ? result.images : [];

  const doInsertImage = (uri, idx) => { onInsertImage(uri); setInsertedImgs(s => ({ ...s, [idx]: true })); };

  return (
    <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-3 sm:p-5">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
            <FileUp style={{ width: 16, height: 16 }} className="text-lime-600" />
            {t('notesPlain.reviewDocumentConversion')}
          </h3>
          <button onClick={onClose} aria-label="Close" className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
        </div>

        <div className="mx-4 sm:mx-5 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-[11.5px] text-amber-800 flex items-start gap-2">
          <AlertTriangle style={{ width: 14, height: 14 }} className="shrink-0 mt-0.5" />
          <span>
            {id
              ? `${meta.pages || '?'} halaman · ${meta.tables_found || 0} tabel · ${meta.images_found || 0} gambar${meta.truncated ? ' · SEBAGIAN DILEWATI (batas halaman)' : ''}. Hasil dirapikan AI — WAJIB diperiksa (angka & tabel) sebelum disisipkan.`
              : `${meta.pages || '?'} pages · ${meta.tables_found || 0} tables · ${meta.images_found || 0} images${meta.truncated ? ' · TRUNCATED (page limit)' : ''}. AI-cleaned — verify (numbers & tables) before inserting.`}
          </span>
        </div>

        <div className="flex gap-1.5 px-4 sm:px-5 pt-3">
          <Btn size="sm" tone={tab === 'edit' ? 'primary' : 'default'} onClick={() => setTab('edit')}><Pencil style={{ width: 12, height: 12 }} /> Edit</Btn>
          <Btn size="sm" tone={tab === 'preview' ? 'primary' : 'default'} onClick={() => setTab('preview')}><Eye style={{ width: 12, height: 12 }} /> {t('notesPlain.preview')}</Btn>
        </div>

        <div className="flex-1 overflow-auto border border-slate-200 rounded-xl mx-4 sm:mx-5 mt-2 mb-3 min-h-[240px]">
          {tab === 'edit' ? (
            <textarea value={md} onChange={e => setMd(e.target.value)}
              className="w-full h-full min-h-[280px] border-none outline-none resize-none p-3 font-mono text-xs leading-relaxed box-border" />
          ) : (
            <div className="p-3 bg-slate-50/60 min-h-[280px]">
              {md.trim() ? <MarkdownView source={md} /> : <span className="text-slate-400 text-sm">{t('notesPlain.empty')}</span>}
            </div>
          )}
        </div>

        {images.length > 0 && (
          <div className="px-4 sm:px-5 pb-2">
            <div className="text-[11.5px] font-semibold text-slate-500 mb-1.5">
              🖼️ {t('notesPlain.imagesLengthClickInsertCursor', {
              length: images.length
            })}
            </div>
            <div className="flex gap-2 flex-wrap max-h-32 overflow-y-auto">
              {images.map((im, ix) => (
                <div key={ix} className="relative w-24 border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                  <img src={im.data_uri} alt={`img-${ix + 1}`} className="w-full h-16 object-cover block" />
                  <button type="button" onClick={() => doInsertImage(im.data_uri, ix)}
                    className={`w-full border-t border-slate-200 text-[10.5px] font-semibold py-1
                      ${insertedImgs[ix] ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-blue-600'}`}>
                    {insertedImgs[ix] ? (t('notesPlain.inserted')) : (t('notesPlain.insert'))}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 justify-end px-4 sm:px-5 py-3 border-t border-slate-100"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
          <Btn onClick={onClose}>{t('notesPlain.close')}</Btn>
          <Btn tone="primary" disabled={!md.trim()} onClick={() => onInsertText(md)}>
            <CheckCircle2 style={{ width: 14, height: 14 }} /> {t('notesPlain.insertTextCursor')}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── PromoteModal — konfirmasi Salin / Pindah ke Notes RAG ─────────────────────
function PromoteModal({ id, note, mode, busy, onConfirm, onClose }) {
  const {
    t
  } = useLang();

  const isMove = mode === 'move';
  return (
    <div className="fixed inset-0 z-[950] bg-slate-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isMove ? 'bg-amber-100' : 'bg-emerald-100'}`}>
            {isMove ? <CornerUpRight style={{ width: 18, height: 18 }} className="text-amber-600" />
                    : <CopyIcon style={{ width: 18, height: 18 }} className="text-emerald-600" />}
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">
              {isMove ? (t('notesPlain.moveNotesRag')) : (t('notesPlain.copyNotesRag'))}
            </h3>
            <p className="text-xs text-slate-400 truncate max-w-[260px]">{note.title || (t('notesPlain.untitled'))}</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          {isMove
            ? (t('notesPlain.noteWillAddedNotesRag'))
            : (t('notesPlain.copyWillAddedNotesRag'))}
        </p>
        <div className="flex gap-2">
          <Btn className="flex-1" onClick={onClose} disabled={busy}>{t('notesPlain.cancel')}</Btn>
          <Btn className="flex-1" tone={isMove ? 'amber' : 'emerald'} onClick={onConfirm} disabled={busy}>
            {busy ? <RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} />
                  : (isMove ? <CornerUpRight style={{ width: 14, height: 14 }} /> : <CopyIcon style={{ width: 14, height: 14 }} />)}
            {isMove ? (t('notesPlain.yesMove')) : (t('notesPlain.yesCopy'))}
          </Btn>
        </div>
      </div>
    </div>
  );
}


// ── Halaman utama ─────────────────────────────────────────────────────────────
// Ambang peringatan ukuran catatan di UI. 2 MB dipilih karena di atas itu
// pemuatan detail dan pengetikan di textarea mulai terasa tersendat; catatan
// terbesar di produksi (01-09-2026) mencapai 11,98 MB.
const NP_WARN_BYTES = 2 * 1024 * 1024;

export default function NotesPlain() {
  const api = useApi();
  const {
    lang,
    locale,
    t
  } = useLang();
  const id = lang !== 'en';
  const isMobile = useIsMobile();

  // `notes` kini berisi RINGKASAN saja (tanpa content/tabs) — lihat catatan di
  // load() dan di server.js pada GET /api/notes-plain. Isi penuh disimpan
  // terpisah di `details`, diambil per catatan saat dibuka.
  const [notes, setNotes]     = useState([]);
  const [details, setDetails] = useState({});     // { [id]: note lengkap }
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailErr, setDetailErr]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);   // { type, msg }
  const [search, setSearch]   = useState('');
  // Hasil pencarian dari server: null = belum/ tidak sedang mencari.
  const [srv, setSrv]         = useState(null);   // { terms:[], byId: Map }
  const [srvBusy, setSrvBusy] = useState(false);
  const [scopeF, setScopeF]   = useState('all');
  // -- Mass download: pilih banyak catatan -> ZIP berisi .md (1 tab = 1 file) --
  const [selMode, setSelMode] = useState(false);
  const [selIds, setSelIds]   = useState(() => new Set());
  const [zipBusy, setZipBusy] = useState(false);
  const [zipMsg, setZipMsg]   = useState(null);   // progres unduh detail sebelum ZIP
  // -- Import ZIP berisi .md -> catatan baru (pratinjau dulu) --
  const [impFile, setImpFile] = useState(null);
  const zipRef = useRef(null);  // all | private | common
  // ── Drag-reorder tab di editor ──
  const [editDragIdx, setEditDragIdx] = useState(null); // index di editing.tabs (0-based) sedang di-drag
  const [editOverIdx, setEditOverIdx] = useState(null);
  const reorderArray = (arr, from, to) => {
    const copy = arr.slice();
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };
  // ── Panel kiri: resizable + collapsible (lebar & status tersimpan) ──
  const [leftOpen, setLeftOpen] = useState(() => {
    try { return localStorage.getItem('notesPlainLeftOpen') !== '0'; } catch (_) { return true; }
  });
  const [leftW, setLeftW] = useState(() => {
    const v = parseInt(localStorage.getItem('notesPlainLeftW') || '320', 10);
    return v >= 240 && v <= 600 ? v : 320;
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const startDrag = (e) => { e.preventDefault(); dragRef.current = { startX: e.clientX, startW: leftW }; setDragging(true); };
  useEffect(() => {
    if (!dragging) return;
    const mm = (e) => {
      const d = dragRef.current; if (!d) return;
      setLeftW(Math.min(600, Math.max(240, d.startW + (e.clientX - d.startX))));
    };
    const mu = () => {
      setDragging(false); dragRef.current = null;
      setLeftW(w => { try { localStorage.setItem('notesPlainLeftW', String(w)); } catch (_) {} return w; });
    };
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', mu);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [dragging]);
  const toggleLeft = () => setLeftOpen(o => {
    try { localStorage.setItem('notesPlainLeftOpen', o ? '0' : '1'); } catch (_) {}
    return !o;
  });
  const [selectedId, setSelectedId] = useState(null);
  const [detailTab, setDetailTab] = useState(0);  // 0 = konten utama, 1..n = tabs
  const [editing, setEditing] = useState(null);   // { id, title, content, scope, tabs, activeTab }
  const [preview, setPreview] = useState(false);
  const [busy, setBusy]       = useState(false);

  // Import dokumen (reuse endpoint /api/notes/pdf-to-md — PDF/DOCX/XLSX)
  const [docBusy, setDocBusy]     = useState(false);
  const [docResult, setDocResult] = useState(null);
  const fileRef     = useRef(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [imgOver,  setImgOver] = useState(false);   // highlight saat drag di atas editor
  const imgFileRef  = useRef(null);
  const textareaRef = useRef(null);

  // Promote copy/move
  const [promote, setPromote] = useState(null);   // { note, mode }
  const [promoteBusy, setPromoteBusy] = useState(false);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  // ── Muat DAFTAR (ringkas) ──────────────────────────────────────────────────
  // Endpoint ini TIDAK lagi mengirim content/tabs. Dulu sekali muat = 36,7 MB
  // (terukur 01-09-2026) karena isi seluruh catatan ikut terbawa; sekarang
  // ~10 KB. Konsekuensinya `notes` hanya boleh dipakai untuk metadata —
  // apa pun yang butuh isi harus lewat `details` / loadDetail().
  const load = useCallback(async () => {
    setLoading(true);
    try { setNotes(await api('/api/notes-plain')); }
    catch (e) { showToast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // Ambil isi penuh satu catatan, dengan cache sederhana di state.
  // `force` dipakai setelah simpan/promote supaya salinan lama tidak nyangkut.
  const loadDetail = useCallback(async (nid, force = false) => {
    if (!nid) return null;
    if (!force && details[nid]) return details[nid];
    const full = await api(`/api/notes-plain/${nid}`);
    setDetails(d => ({ ...d, [nid]: full }));
    return full;
  }, [api, details]);

  const dropDetail = useCallback((nid) => {
    setDetails(d => { const c = { ...d }; delete c[nid]; return c; });
  }, []);

  const inScope = notes.filter(n => scopeF === 'all' || n.scope === scopeF);

  // ── Pencarian — DILAYANI SERVER (GET /api/notes-plain/search) ──────────────
  // Sebelumnya pencarian berjalan di browser dengan menelusuri isi seluruh
  // catatan; itu hanya mungkin karena daftar membawa 36,7 MB. Kini server
  // yang memindai, memakai indeks in-memory yang membuang data-URI base64.
  // Beda perilaku yang disengaja: kata pencarian tidak lagi bisa "cocok"
  // dengan potongan acak di dalam base64 gambar.
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const ql = terms.length > 0;

  useEffect(() => {
    const q = search.trim();
    if (!q) { setSrv(null); setSrvBusy(false); return; }
    let alive = true;
    setSrvBusy(true);
    // Debounce: hindari satu permintaan per ketikan.
    const h = setTimeout(async () => {
      try {
        const r = await api(`/api/notes-plain/search?q=${encodeURIComponent(q)}`);
        if (!alive) return;
        const byId = new Map((r.results || []).map(x => [x.id, x]));
        setSrv({ byId });
      } catch (e) {
        if (alive) { setSrv({ byId: new Map() }); showToast(e.message, 'error'); }
      } finally {
        if (alive) setSrvBusy(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [search, api]);

  // Gabungkan hasil server dengan daftar (filter scope tetap di klien).
  // Saat pencarian masih berjalan, daftar lama dipertahankan agar tidak
  // berkedip kosong di antara ketikan.
  const withMatch = !ql
    ? inScope.map(n => ({ n, m: { ok: true, rank: 0, tabIdx: 0, snippet: '' } }))
    : (srv
        ? inScope.filter(n => srv.byId.has(n.id)).map(n => ({ n, m: srv.byId.get(n.id) }))
        : []);
  const sorted = [...withMatch].sort((a, b) => {
    if (ql && a.m.rank !== b.m.rank) return a.m.rank - b.m.rank;
    return (b.n.updatedAt || '').localeCompare(a.n.updatedAt || '');
  });
  const filtered = sorted; // { n, m } — dipakai render daftar (lihat map di bawah)

  // -- Mass download --
  const visibleIds = filtered.map(({ n }) => n.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(x => selIds.has(x));
  const toggleSel = (nid) => setSelIds(prev => {
    const s = new Set(prev);
    if (s.has(nid)) s.delete(nid); else s.add(nid);
    return s;
  });
  const toggleSelectAll = () => setSelIds(prev => {
    const s = new Set(prev);
    if (allVisibleSelected) visibleIds.forEach(x => s.delete(x));
    else visibleIds.forEach(x => s.add(x));
    return s;
  });
  const exitSelMode = () => { setSelMode(false); setSelIds(new Set()); };
  const downloadSelected = async () => {
    const pickedMeta = notes.filter(n => selIds.has(n.id));
    if (!pickedMeta.length) { showToast(id ? 'Belum ada catatan yang dipilih.' : 'No notes selected.', 'error'); return; }
    setZipBusy(true);
    try {
      // Daftar tidak lagi membawa isi, jadi isi penuh diambil per catatan
      // dulu. Berurutan (bukan Promise.all) supaya beberapa catatan besar
      // tidak dikirim serentak — 5 catatan terbesar saja ±34 MB.
      const picked = [];
      for (let i = 0; i < pickedMeta.length; i++) {
        setZipMsg(`${i + 1}/${pickedMeta.length}`);
        picked.push(await loadDetail(pickedMeta[i].id));
      }
      setZipMsg(null);
      const r = await downloadNotesZip(picked, { prefix: 'notes-plain' });
      showToast(id
        ? (picked.length + ' catatan diunduh (' + r.files + ' berkas .md) - ' + r.name)
        : (picked.length + ' notes downloaded (' + r.files + ' .md files) - ' + r.name));
      exitSelMode();
    } catch (e) {
      showToast((id ? 'Gagal membuat ZIP: ' : 'Failed to build ZIP: ') + (e?.message || e), 'error');
    } finally { setZipBusy(false); setZipMsg(null); }
  };
  // Isi penuh datang dari cache `details`; `notes` hanya ringkasan.
  const selected = (selectedId && details[selectedId]) || null;
  const selectedMeta = notes.find(n => n.id === selectedId) || null;

  // Ambil isi begitu sebuah catatan dipilih.
  useEffect(() => {
    if (!selectedId || details[selectedId]) { setDetailErr(null); return; }
    let alive = true;
    setDetailBusy(true); setDetailErr(null);
    loadDetail(selectedId)
      .catch(e => { if (alive) setDetailErr(e.message); })
      .finally(() => { if (alive) setDetailBusy(false); });
    return () => { alive = false; };
  }, [selectedId, details, loadDetail]);

  // Klik hasil pencarian → buka note, pindah ke tab match, siap di-highlight.
  const [pendingHl, setPendingHl] = useState(null);
  const viewerContentRef = useRef(null);
  const openSearchResult = (n, m) => {
    setEditing(null);
    setSelectedId(n.id);
    setDetailTab(ql ? (m.tabIdx || 0) : 0);
    if (ql) setPendingHl(m.snippet || terms[0]); else setPendingHl(null);
  };
  // Setelah viewer render dgn highlight snippet aktif, scroll ke <mark> pertama
  // lalu bersihkan pendingHl (sekali pakai, seperti di Notes RAG).
  useEffect(() => {
    if (!pendingHl || !selectedId) return;
    const t = setTimeout(() => {
      const root = viewerContentRef.current;
      const mark = root?.querySelector('mark[data-pvhl="true"]') || root?.querySelector('mark');
      if (mark) mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingHl(null);
    }, 120);
    return () => clearTimeout(t);
  }, [pendingHl, selectedId, detailTab]);

  // ── "Cari di catatan" (mode BACA) — kotak pencarian dgn navigasi ▲▼ +
  // highlight, DAN cari lintas-TAB (disamakan dgn Notes RAG): kalau term tak
  // ada di tab aktif tapi ada di tab lain, otomatis pindah + tampilkan chip
  // "Juga ada di" utk lompat manual. ──
  const [pvSearch,   setPvSearch]   = useState('');
  const [pvMatchIdx, setPvMatchIdx] = useState(0);
  const [pvTotal,    setPvTotal]    = useState(0);
  const [pvTabHits,  setPvTabHits]  = useState([]);
  const pvSearchRef  = useRef(null);

  useEffect(() => { setPvSearch(''); setPvMatchIdx(0); setPvTotal(0); setPvTabHits([]); }, [selectedId]);

  useEffect(() => {
    const term = pvSearch.trim().toLowerCase();
    if (!term || !selected) { setPvTabHits([]); return; }
    const segs = [
      { idx: 0, label: selected.title || (t('notesPlain.main')), text: `${selected.title || ''}\n${selected.content || ''}` },
      ...(selected.tabs || []).map((t, i) => ({ idx: i + 1, label: t.title || `Tab ${i + 1}`, text: `${t.title || ''}\n${t.content || ''}` })),
    ];
    const matching = segs.filter(s => s.text.toLowerCase().includes(term));
    const activeMatches = matching.some(s => s.idx === detailTab);
    if (!activeMatches && matching.length) setDetailTab(matching[0].idx);
    setPvTabHits(matching);
    // sengaja TIDAK depend `detailTab` — cegah loop saat efek ini sendiri memanggil setDetailTab.
    // eslint-disable-next-line
  }, [pvSearch, selectedId, selected]);

  useEffect(() => {
    const root = viewerContentRef.current;
    if (!root) return;
    const marks = Array.from(root.querySelectorAll('mark[data-pvhl]'));
    setPvTotal(marks.length);
    if (!marks.length) { setPvMatchIdx(0); return; }
    setPvMatchIdx(0);
    marks.forEach((m, i) => {
      m.style.background = i === 0 ? '#f97316' : '#fef08a';
      m.style.color       = i === 0 ? '#fff'    : '#0f172a';
    });
    if (marks[0]) marks[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [pvSearch, selectedId, detailTab]);

  const pvNav = (dir) => {
    const root = viewerContentRef.current;
    if (!root) return;
    const marks = Array.from(root.querySelectorAll('mark[data-pvhl]'));
    if (!marks.length) return;
    const next = (pvMatchIdx + dir + marks.length) % marks.length;
    setPvMatchIdx(next);
    marks.forEach((m, i) => {
      m.style.background = i === next ? '#f97316' : '#fef08a';
      m.style.color       = i === next ? '#fff'    : '#0f172a';
    });
    marks[next].scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // ── Helper tabs (0 = konten utama, 1..n = tabs) ──
  const getActiveContent = (e) => e.activeTab === 0 ? (e.content || '') : (e.tabs?.[e.activeTab - 1]?.content || '');
  const setActiveContent = (e, val) => e.activeTab === 0
    ? { ...e, content: val }
    : { ...e, tabs: e.tabs.map((t, i) => i === e.activeTab - 1 ? { ...t, content: val } : t) };
  const updateActiveContent = (val) => setEditing(s => (s ? setActiveContent(s, val) : s));

  // ── Cari/Ganti + Bantuan Markdown (disamakan dgn Notes RAG) ──
  const [showSR,     setShowSR]     = useState(false);
  const [srFind,     setSrFind]     = useState('');
  const [srReplace,  setSrReplace]  = useState('');
  const [srMatchIdx, setSrMatchIdx] = useState(-1);
  const [srCount,    setSrCount]    = useState(null);
  const [showHelp,   setShowHelp]   = useState(false);

  const openSR  = useCallback(() => { setShowSR(true); setSrMatchIdx(-1); setSrCount(null); }, []);
  const closeSR = useCallback(() => { setShowSR(false); setSrFind(''); setSrReplace(''); setSrMatchIdx(-1); setSrCount(null); }, []);

  const srFindNext = useCallback(() => {
    if (!srFind || !editing) return;
    const content = getActiveContent(editing);
    const lower   = content.toLowerCase();
    const nLower  = srFind.toLowerCase();
    const indices = [];
    let pos = 0;
    while ((pos = lower.indexOf(nLower, pos)) !== -1) { indices.push(pos); pos += nLower.length; }
    setSrCount(indices.length);
    if (indices.length === 0) { setSrMatchIdx(-1); return; }
    const next = (srMatchIdx + 1) % indices.length;
    setSrMatchIdx(next);
    const ta = textareaRef.current;
    if (ta) {
      ta.focus();
      ta.setSelectionRange(indices[next], indices[next] + srFind.length);
      const linesBefore = content.slice(0, indices[next]).split('\n').length;
      ta.scrollTop = Math.max(0, (linesBefore - 3) * 18);
    }
  }, [srFind, srMatchIdx, editing]);

  const srReplaceOne = useCallback(() => {
    if (!srFind || !editing) return;
    const ta = textareaRef.current;
    if (!ta) return;
    const { selectionStart, selectionEnd } = ta;
    const cur = getActiveContent(editing);
    const sel = cur.slice(selectionStart, selectionEnd);
    if (sel.toLowerCase() === srFind.toLowerCase()) {
      updateActiveContent(cur.slice(0, selectionStart) + srReplace + cur.slice(selectionEnd));
      setSrMatchIdx(-1); setSrCount(null);
    } else {
      srFindNext();
    }
  }, [srFind, srReplace, editing, srFindNext]);

  const srReplaceAll = useCallback(() => {
    if (!srFind || !editing) return;
    const regex = new RegExp(srFind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const cur = getActiveContent(editing);
    const count = (cur.match(regex) || []).length;
    updateActiveContent(cur.replace(regex, srReplace));
    setSrCount(count); setSrMatchIdx(-1);
  }, [srFind, srReplace, editing]);

  // ── Insert sintaks markdown di posisi kursor ──
  const insertMdAtCursor = useCallback((before, after = '', placeholder = '') => {
    const ta = textareaRef.current;
    if (!ta || !editing) return;
    ta.focus();
    const { selectionStart: ss, selectionEnd: se } = ta;
    const cur = getActiveContent(editing);
    const sel = cur.slice(ss, se) || placeholder;
    updateActiveContent(cur.slice(0, ss) + before + sel + after + cur.slice(se));
    setTimeout(() => {
      ta.focus();
      const newPos = ss + before.length + sel.length + after.length;
      ta.setSelectionRange(newPos, newPos);
    }, 0);
  }, [editing]);

  // Cheatsheet markdown — [label, before, after, placeholder, contoh tampil]
  const MD_HELP = [
    { group: t('notesPlain.text'), items: [
      { label: 'Bold',        before: '**', after: '**', ph: t('notesPlain.boldText'),   preview: '**tebal**' },
      { label: 'Italic',      before: '*',  after: '*',  ph: t('notesPlain.italicText'), preview: '*miring*' },
      { label: 'Code inline', before: '`',  after: '`',  ph: 'kode',         preview: '`kode`' },
      { label: 'Bold+Italic', before: '***',after: '***',ph: 'teks',         preview: '***teks***' },
    ]},
    { group: 'Heading', items: [
      { label: 'H1', before: '# ',    after: '', ph: t('notesPlain.title1'), preview: '# H1' },
      { label: 'H2', before: '## ',   after: '', ph: t('notesPlain.title2'), preview: '## H2' },
      { label: 'H3', before: '### ',  after: '', ph: t('notesPlain.title3'), preview: '### H3' },
    ]},
    { group: 'List', items: [
      { label: t('notesPlain.bullet'),   before: '- ',   after: '', ph: 'item', preview: '- item' },
      { label: t('notesPlain.numbered'), before: '1. ',  after: '', ph: 'item', preview: '1. item' },
      { label: 'Checkbox',                     before: '- [ ] ',after:'', ph: 'task', preview: '- [ ] task' },
    ]},
    { group: t('notesPlain.block'), items: [
      { label: t('notesPlain.blockquote'), before: '> ',    after: '',    ph: t('notesPlain.quote'), preview: '> teks' },
      { label: t('notesPlain.codeBlock'), before: '```\n', after: '\n```',ph: t('notesPlain.codeHere'), preview: '```…```' },
      { label: t('notesPlain.svgBlock'),  before: '```svg\n', after: '\n```', ph: '<svg>…</svg>', preview: '```svg…```' },
      { label: t('notesPlain.hr'),          before: '\n---\n',after: '',   ph: '',           preview: '---' },
    ]},
    { group: t('notesPlain.linkTable'), items: [
      { label: 'Link',  before: '[',  after: '](url)', ph: t('notesPlain.linkText'), preview: '[teks](url)' },
      { label: t('notesPlain.table'),
        before: '| Kolom 1 | Kolom 2 |\n|---------|----------|\n| ',
        after:  ' | nilai |\n',
        ph: 'nilai', preview: '| … |' },
    ]},
  ];

  // ── CRUD ──
  const startNew  = () => { setSelectedId(null); setDetailTab(0); setPreview(false); setEditing({ id: null, title: '', content: '', scope: 'private', tabs: [], activeTab: 0 }); };
  const startEdit = (n) => {
    setPreview(false);
    setEditing({
      id: n.id, title: n.title, content: n.content, scope: n.scope || 'private',
      tabs: (n.tabs || []).map(t => ({ title: t.title || '', content: t.content || '' })),
      activeTab: Math.min(detailTab, (n.tabs || []).length),
    });
  };

  const save = async () => {
    const e = editing;
    const hasTabContent = (e.tabs || []).some(t => (t.title || '').trim() || (t.content || '').trim());
    if (!e.title.trim() && !e.content.trim() && !hasTabContent) { showToast(t('notesPlain.titleContentRequired'), 'error'); return; }
    setBusy(true);
    try {
      const payload = { title: e.title, content: e.content, scope: e.scope, tabs: (e.tabs || []).map(t => ({ title: t.title, content: t.content })) };
      let newId = e.id;
      if (e.id) await api(`/api/notes-plain/${e.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      else { const r = await api('/api/notes-plain', { method: 'POST', body: JSON.stringify(payload) }); newId = r.id; }
      showToast(t('notesPlain.noteSaved'));
      setDetailTab(e.activeTab || 0);
      setEditing(null); setSelectedId(newId || null);
      // Cache detail wajib dibuang, kalau tidak viewer menampilkan isi lama.
      if (newId) dropDetail(newId);
      await load();
    } catch (err) { showToast(err.message, 'error'); }
    finally { setBusy(false); }
  };

  const remove = async (n) => {
    if (!window.confirm(id ? `Hapus catatan "${n.title || '(tanpa judul)'}"?` : `Delete note "${n.title || '(untitled)'}"?`)) return;
    try {
      await api(`/api/notes-plain/${n.id}`, { method: 'DELETE' });
      showToast(t('notesPlain.noteDeleted'));
      dropDetail(n.id);
      if (selectedId === n.id) setSelectedId(null);
      if (editing?.id === n.id) setEditing(null);
      await load();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const doPromote = async () => {
    if (!promote) return;
    setPromoteBusy(true);
    try {
      const r = await api(`/api/notes-plain/${promote.note.id}/promote`, { method: 'POST', body: JSON.stringify({ mode: promote.mode }) });
      showToast(r.message || 'OK');
      dropDetail(promote.note.id);
      if (promote.mode === 'move' && selectedId === promote.note.id) setSelectedId(null);
      setPromote(null);
      await load();
    } catch (err) { showToast(err.message, 'error'); }
    finally { setPromoteBusy(false); }
  };

  // ── Import dokumen → sisip markdown di kursor editor (tab aktif) ──
  const insertAtCursor = (text) => {
    setEditing(cur => {
      if (!cur) return cur;
      const ta = textareaRef.current;
      const c = getActiveContent(cur);
      const pos = ta && typeof ta.selectionStart === 'number' ? ta.selectionStart : c.length;
      return setActiveContent(cur, c.slice(0, pos) + text + c.slice(pos));
    });
  };

  const onPickDoc = () => {
    // Import butuh editor aktif (agar hasil bisa disisipkan). Bila belum, buka catatan baru.
    if (!editing) startNew();
    fileRef.current?.click();
  };

  const onDocSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(pdf|docx|xlsx)$/i.test(file.name)) { showToast(t('notesPlain.pdfWordDocxExcelXlsx'), 'error'); return; }
    if (file.size > 10 * 1024 * 1024) { showToast(t('notesPlain.max10mb'), 'error'); return; }
    setDocBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosApi.post('/api/notes/pdf-to-md', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
      });
      setDocResult({ markdown: res.data.markdown || '', images: Array.isArray(res.data.images) ? res.data.images : [], meta: res.data.meta || {} });
      // Prefill judul dari nama file bila catatan baru masih kosong.
      setEditing(cur => (cur && !cur.id && !cur.title.trim())
        ? { ...cur, title: file.name.replace(/\.(pdf|docx|xlsx)$/i, '') } : cur);
    } catch (err) {
      showToast(err.response?.data?.error || err.message || (t('notesPlain.conversionFailed')), 'error');
    } finally { setDocBusy(false); }
  };

  // ── Sisip gambar → data URI di kursor editor (tab aktif) ──
  // Sumber bisa: tombol toolbar, paste (Ctrl+V screenshot), atau drag-drop.
  // Semua bermuara ke sini agar validasi & downscale-nya satu jalur.
  const insertImageFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(isImageFile);
    if (!files.length) { showToast(t('notesPlain.imageOnly'), 'error'); return; }
    if (files.some(f => f.size > IMG_MAX_BYTES)) { showToast(t('notesPlain.imageMax5mb'), 'error'); return; }
    setImgBusy(true);
    try {
      const uris = [];
      for (const f of files) uris.push(await imageFileToDataUri(f));
      // Satu kali insert untuk semua gambar — kalau di-insert satu per satu,
      // posisi kursor belum bergerak sehingga urutannya jadi terbalik.
      const chunk = '\n\n' + uris.map(u => `<img src="${u}" style="max-width:100%" />`).join('\n\n') + '\n\n';
      insertAtCursor(chunk);
      const bytes = uris.reduce((a, u) => a + Math.floor((u.length - (u.indexOf(',') + 1)) * 0.75), 0);
      showToast(t('notesPlain.imageInserted', { count: uris.length, size: humanBytes(bytes) }), 'success');
    } catch (err) {
      // Format tidak bisa di-decode browser (HEIC/HEIF dsb) → beri tahu jelas,
      // JANGAN sisipkan gambar yang pasti tampil kosong.
      if (err?.message === 'IMG_DECODE_FAILED') {
        showToast(t('notesPlain.imageUnsupportedFormat', { name: err.fileName || '' }), 'error');
      } else {
        showToast(err?.message || t('notesPlain.imageFailed'), 'error');
      }
    } finally { setImgBusy(false); }
  };

  const onPickImage = () => {
    if (!editing) startNew();
    imgFileRef.current?.click();
  };

  const onImageSelected = (e) => {
    // PENTING: salin dulu jadi array. `e.target.files` adalah referensi LIVE ke
    // FileList milik input — begitu value di-reset (agar file yang SAMA bisa
    // dipilih ulang), list yang sama ikut kosong dan insertImageFiles tak
    // pernah terpanggil. Gagalnya SENYAP: tanpa alert, tanpa error console.
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length) insertImageFiles(files);
  };

  // Paste: hanya tangani bila clipboard memang berisi gambar; kalau teks biasa
  // biarkan perilaku bawaan textarea (jangan preventDefault).
  const onEditorPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imgs = items.filter(it => it.kind === 'file' && /^image\//i.test(it.type))
      .map(it => it.getAsFile()).filter(Boolean);
    if (!imgs.length) return;
    e.preventDefault();
    insertImageFiles(imgs);
  };

  const onEditorDrop = (e) => {
    const files = Array.from(e.dataTransfer?.files || []).filter(f => /^image\//i.test(f.type));
    setImgOver(false);
    if (!files.length) return;   // biarkan drop non-gambar (mis. drag teks) apa adanya
    e.preventDefault();
    insertImageFiles(files);
  };

  const onEditorDragOver = (e) => {
    const hasFile = Array.from(e.dataTransfer?.types || []).includes('Files');
    if (!hasFile) return;
    e.preventDefault();
    setImgOver(true);
  };

  // ── Panel kanan: viewer / editor ──
  const panel = editing ? (
    <div className="flex flex-col h-full">
      {/* Toolbar editor */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60">
        {isMobile && (
          <Btn size="sm" onClick={() => setEditing(null)}><ArrowLeft style={{ width: 13, height: 13 }} /> {t('notesPlain.back')}</Btn>
        )}
        <span className="font-semibold text-slate-700 text-sm">{editing.id ? (t('notesPlain.editNote')) : (t('notesPlain.newNote'))}</span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Btn size="sm" tone={preview ? 'primary' : 'default'} onClick={() => setPreview(p => !p)}>
            <Eye style={{ width: 13, height: 13 }} /> {t('notesPlain.preview')}
          </Btn>
          {!preview && (
            <Btn size="sm" tone={showSR ? 'primary' : 'default'} onClick={() => (showSR ? closeSR() : openSR())} title="Ctrl+H">
              <Search style={{ width: 13, height: 13 }} /> {t('notesPlain.findReplace')}
            </Btn>
          )}
          {!preview && (
            <Btn size="sm" tone={showHelp ? 'primary' : 'default'} onClick={() => setShowHelp(h => !h)} title={t('notesPlain.markdownHelp')}>
              ? {t('notesPlain.help')}
            </Btn>
          )}
          {!preview && (
            <Btn size="sm" onClick={onPickImage} disabled={imgBusy}
              title={t('notesPlain.insertImageHint')}>
              {imgBusy ? <RefreshCw className="animate-spin" style={{ width: 13, height: 13 }} /> : <ImageIcon style={{ width: 13, height: 13 }} />}
              {t('notesPlain.insertImage')}
            </Btn>
          )}
          <Btn size="sm" onClick={onPickDoc} disabled={docBusy}
            title={t('notesPlain.importPdfWordExcelAs')}>
            {docBusy ? <RefreshCw className="animate-spin" style={{ width: 13, height: 13 }} /> : <FileUp style={{ width: 13, height: 13 }} />}
            {docBusy ? (t('notesPlain.converting')) : (t('notesPlain.importDocument'))}
          </Btn>
          <Btn size="sm" tone="primary" onClick={save} disabled={busy}>
            {busy ? <RefreshCw className="animate-spin" style={{ width: 13, height: 13 }} /> : <Save style={{ width: 13, height: 13 }} />} {t('notesPlain.save')}
          </Btn>
          {!isMobile && <Btn size="sm" onClick={() => setEditing(null)}><X style={{ width: 13, height: 13 }} /></Btn>}
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3 flex-1 min-h-0">
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })}
            placeholder={t('notesPlain.noteTitle')}
            className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
          <select value={editing.scope} onChange={e => setEditing({ ...editing, scope: e.target.value })}
            className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white outline-none focus:border-blue-400">
            <option value="private">{t('notesPlain.private2')}</option>
            <option value="common">{t('notesPlain.shared2')}</option>
          </select>
        </div>

        {/* Tab bar editor: 0 = utama, 1..n = tabs. Tambah / ganti nama / hapus / geser urutan. */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {[{ title: editing.title || (t('notesPlain.main')) }, ...(editing.tabs || [])].map((t, idx) => {
            const act = editing.activeTab === idx;
            const isOver = idx > 0 && editOverIdx === idx - 1 && editDragIdx !== null && editDragIdx !== idx - 1;
            const isDragged = idx > 0 && editDragIdx === idx - 1;
            return (
              <button key={idx} onClick={() => setEditing({ ...editing, activeTab: idx })}
                draggable={idx > 0}
                onDragStart={idx > 0 ? (e) => { setEditDragIdx(idx - 1); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx - 1)); } : undefined}
                onDragOver={idx > 0 ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (editDragIdx !== null) setEditOverIdx(idx - 1); } : undefined}
                onDragLeave={idx > 0 ? () => setEditOverIdx(o => (o === idx - 1 ? null : o)) : undefined}
                onDrop={idx > 0 ? (e) => {
                  e.preventDefault();
                  const to = idx - 1;
                  setEditing(s => {
                    if (editDragIdx === null || editDragIdx === to) return s;
                    const tabs = reorderArray(s.tabs || [], editDragIdx, to);
                    const na = s.activeTab === editDragIdx + 1 ? to + 1 : s.activeTab;
                    return { ...s, tabs, activeTab: na };
                  });
                  setEditDragIdx(null); setEditOverIdx(null);
                } : undefined}
                onDragEnd={() => { setEditDragIdx(null); setEditOverIdx(null); }}
                title={idx === 0 ? (id ? 'Tab utama (judul catatan)' : 'Main tab (note title)') : (id ? `${t.title || `Tab ${idx}`} — tahan & geser untuk pindah urutan` : `${t.title || `Tab ${idx}`} — drag to reorder`)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-semibold max-w-[180px] transition-colors
                  ${idx > 0 ? 'cursor-grab' : ''} ${isOver ? 'outline outline-2 outline-dashed outline-blue-400' : ''} ${isDragged ? 'opacity-40' : ''}
                  ${act ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                {idx > 0 && <span className="text-slate-300 text-[11px]" style={{ letterSpacing: -1 }}>⠿</span>}
                <span className="truncate">{idx === 0 ? `📄 ${t.title}` : (t.title || `Tab ${idx}`)}</span>
                {idx > 0 && act && (
                  <span role="button" title={id ? 'Hapus tab' : 'Remove tab'}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      if (!window.confirm(id ? `Hapus tab "${t.title || `Tab ${idx}`}"?` : `Remove tab "${t.title || `Tab ${idx}`}"?`)) return;
                      const tabs = editing.tabs.filter((_, i) => i !== idx - 1);
                      setEditing({ ...editing, tabs, activeTab: Math.min(idx - 1, tabs.length) });
                    }}
                    className="text-slate-400 hover:text-rose-500 leading-none">×</span>
                )}
              </button>
            );
          })}
          <button onClick={() => {
              const tabs = [...(editing.tabs || []), { title: '', content: '' }];
              setEditing({ ...editing, tabs, activeTab: tabs.length });
            }}
            className="px-2.5 py-1.5 rounded-lg border border-dashed border-slate-300 text-xs font-semibold text-slate-400 hover:text-blue-600 hover:border-blue-300"
            title={t('notesPlain.addTab')}>＋ Tab</button>
        </div>
        {(editing.tabs || []).length > 0 && (
          <p className="text-[11px] text-slate-400">
            💡 {t('notesPlain.holdTabThenDragReorder')}
          </p>
        )}
        {editing.activeTab > 0 && (
          <input value={editing.tabs[editing.activeTab - 1]?.title || ''}
            onChange={e => setEditing({ ...editing, tabs: editing.tabs.map((t, i) => i === editing.activeTab - 1 ? { ...t, title: e.target.value } : t) })}
            placeholder={t('notesPlain.tabActivetabTitle', {
              activeTab: editing.activeTab
            })}
            className="px-3.5 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
        )}

        {preview ? (
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-xl p-4 bg-slate-50/50 min-h-[300px]">
            {getActiveContent(editing).trim() ? <MarkdownView source={getActiveContent(editing)} /> : <span className="text-slate-400 text-sm">{t('notesPlain.empty')}</span>}
          </div>
        ) : (
          <>
            {/* Cari/Ganti */}
            {showSR && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mb-1.5 flex flex-wrap gap-1.5 items-center">
                <input value={srFind} onChange={e => { setSrFind(e.target.value); setSrMatchIdx(-1); setSrCount(null); }}
                  placeholder={t('notesPlain.find')}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); srFindNext(); } if (e.key === 'Escape') closeSR(); }}
                  className="w-36 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-blue-400" autoFocus />
                <input value={srReplace} onChange={e => setSrReplace(e.target.value)}
                  placeholder={t('notesPlain.replace')}
                  onKeyDown={e => { if (e.key === 'Escape') closeSR(); }}
                  className="w-36 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-blue-400" />
                <Btn size="sm" onClick={srFindNext} disabled={!srFind}>{t('notesPlain.find2')}</Btn>
                <Btn size="sm" onClick={srReplaceOne} disabled={!srFind}>{t('notesPlain.replace2')}</Btn>
                <Btn size="sm" tone="primary" onClick={srReplaceAll} disabled={!srFind}>{t('notesPlain.replaceAll')}</Btn>
                {srCount !== null && (
                  <span className={`text-xs font-semibold ${srCount === 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                    {srCount === 0 ? (t('notesPlain.notFound')) : (id ? `${srCount} hasil` : `${srCount} match${srCount !== 1 ? 'es' : ''}`)}
                  </span>
                )}
                <button onClick={closeSR} className="ml-auto text-slate-400 hover:text-slate-600 text-base leading-none">×</button>
              </div>
            )}

            {/* Bantuan Markdown */}
            {showHelp && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 mb-1.5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold text-slate-500">📖 {t('notesPlain.markdownReferenceClickInsert')}</span>
                  <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-slate-600 text-base leading-none">×</button>
                </div>
                <div className="flex flex-col gap-2">
                  {MD_HELP.map(group => (
                    <div key={group.group}>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">{group.group}</div>
                      <div className="flex flex-wrap gap-1">
                        {group.items.map(item => (
                          <button key={item.label} onClick={() => insertMdAtCursor(item.before, item.after, item.ph)} title={item.preview}
                            className="flex flex-col items-start gap-0.5 bg-white border border-slate-200 rounded-md px-2 py-1 font-mono text-[11px] text-slate-700 hover:bg-blue-50 hover:border-blue-200 transition-colors">
                            <span className="text-[10px] text-slate-500 font-sans font-semibold">{item.label}</span>
                            <span className="text-blue-600">{item.preview}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-2 pt-1.5 border-t border-slate-200">
                  💡 {t('notesPlain.selectTextEditorFirstThen')}
                </p>
              </div>
            )}

            <textarea ref={textareaRef} value={getActiveContent(editing)}
              onChange={e => setEditing(cur => setActiveContent(cur, e.target.value))}
              onKeyDown={e => { if (e.key === 'Escape' && showSR) closeSR(); }}
              onPaste={onEditorPaste}
              onDragOver={onEditorDragOver}
              onDragLeave={() => setImgOver(false)}
              onDrop={onEditorDrop}
              placeholder={t('notesPlain.writeNoteMarkdown')}
              className={`flex-1 w-full min-h-[300px] px-3.5 py-3 rounded-xl border text-[13px] font-mono leading-relaxed outline-none resize-none focus:ring-2 focus:ring-blue-100
                ${imgOver ? 'border-blue-400 ring-2 ring-blue-200 bg-blue-50/40' : 'border-slate-200 focus:border-blue-400'}`} />
          </>
        )}
      </div>
    </div>
  ) : (selectedMeta && !selected) ? (
    // Isi catatan diambil terpisah dari daftar, jadi ada jeda singkat di sini.
    // Catatan besar (belasan MB) bisa perlu beberapa detik — tampilkan judul
    // dan ukurannya supaya jelas apa yang sedang dimuat.
    <div className="h-full flex items-center justify-center text-center p-10">
      <div>
        {detailErr ? (
          <>
            <AlertTriangle style={{ width: 34, height: 34 }} className="mx-auto mb-3 text-rose-300" />
            <p className="text-sm text-rose-600 font-medium">{detailErr}</p>
            <button onClick={() => loadDetail(selectedMeta.id, true).catch(e => setDetailErr(e.message))}
              className="mt-3 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:border-slate-300">
              {id ? 'Coba lagi' : 'Retry'}
            </button>
          </>
        ) : (
          <>
            <RefreshCw style={{ width: 28, height: 28 }} className="animate-spin mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500 font-medium truncate max-w-[280px] mx-auto">
              {selectedMeta.title || (t('notesPlain.untitled'))}
            </p>
            <p className="text-xs text-slate-400 mt-1">{humanBytes(selectedMeta.sizeBytes || 0)}</p>
          </>
        )}
      </div>
    </div>
  ) : selected ? (
    <div className="flex flex-col h-full">
      {/* Peringatan ukuran — catatan raksasa memperlambat pemuatan & editor. */}
      {(selectedMeta?.sizeBytes || 0) >= NP_WARN_BYTES && (
        <div className="flex items-start gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800">
          <AlertTriangle style={{ width: 13, height: 13 }} className="shrink-0 mt-0.5" />
          <span>
            {id
              ? `Catatan ini berukuran ${humanBytes(selectedMeta.sizeBytes)} — pemuatan dan penyuntingan akan terasa lambat. Pertimbangkan memecahnya menjadi beberapa catatan, atau kurangi gambar yang ditempel (gambar disimpan sebagai base64 di dalam catatan).`
              : `This note is ${humanBytes(selectedMeta.sizeBytes)} — loading and editing will feel slow. Consider splitting it, or removing pasted images (images are stored as base64 inside the note).`}
          </span>
        </div>
      )}
      {/* Toolbar viewer */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60">
        {isMobile && (
          <Btn size="sm" onClick={() => setSelectedId(null)}><ArrowLeft style={{ width: 13, height: 13 }} /> {t('notesPlain.back')}</Btn>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800 text-sm truncate">{selected.title || (t('notesPlain.untitled'))}</h3>
            <ScopeBadge scope={selected.scope} id={id} />
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {t('notesPlain.updated')} {fmtDt(selected.updatedAt, locale)}{selected.updatedBy ? ` · ${selected.updatedBy}` : ''}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Btn size="sm" onClick={() => startEdit(selected)}><Pencil style={{ width: 13, height: 13 }} /> Edit</Btn>
          <Btn size="sm" tone="emerald" onClick={() => setPromote({ note: selected, mode: 'copy' })}
            title={t('notesPlain.copyNotesRagIndexedInto')}>
            <CopyIcon style={{ width: 13, height: 13 }} /> {t('notesPlain.copyRag')}
          </Btn>
          <Btn size="sm" tone="amber" onClick={() => setPromote({ note: selected, mode: 'move' })}
            title={t('notesPlain.moveNotesRagRemovedPlain')}>
            <CornerUpRight style={{ width: 13, height: 13 }} /> {t('notesPlain.moveRag')}
          </Btn>
          <Btn size="sm" tone="danger" onClick={() => remove(selected)}><Trash2 style={{ width: 13, height: 13 }} /></Btn>
        </div>
      </div>
      <div ref={viewerContentRef} className="flex-1 overflow-y-auto p-4 sm:p-5">
        {(selected.tabs && selected.tabs.length > 0) && (
          <div className="flex items-center gap-1.5 flex-wrap mb-4">
            {[{ title: selected.title || (t('notesPlain.main')) }, ...selected.tabs].map((t, idx) => (
              <button key={idx} onClick={() => setDetailTab(idx)}
                className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs font-semibold max-w-[180px] transition-colors
                  ${detailTab === idx ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                <span className="truncate">{idx === 0 ? `📄 ${t.title}` : (t.title || `Tab ${idx}`)}</span>
              </button>
            ))}
          </div>
        )}
        {/* Cari di catatan — navigasi ▲▼ + lintas-tab */}
        <div className="flex items-center gap-1.5 mb-2">
          <div className="relative flex-1 max-w-[280px]">
            <Search style={{ width: 12, height: 12 }} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input ref={pvSearchRef} value={pvSearch}
              onChange={e => { setPvSearch(e.target.value); setPvMatchIdx(0); }}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); pvNav(e.shiftKey ? -1 : 1); }
                if (e.key === 'Escape') { setPvSearch(''); setPvMatchIdx(0); setPvTotal(0); }
              }}
              placeholder={t('notesPlain.searchNote')}
              className="w-full pl-6 pr-6 py-1 text-xs border border-slate-200 rounded-lg outline-none focus:border-blue-400" />
            {pvSearch && (
              <button onClick={() => { setPvSearch(''); setPvMatchIdx(0); setPvTotal(0); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-sm leading-none">×</button>
            )}
          </div>
          {pvSearch && (
            <>
              <span className={`text-[11px] font-semibold whitespace-nowrap ${pvTotal === 0 ? 'text-rose-500' : 'text-slate-500'}`}>
                {pvTotal === 0 ? (t('notesPlain.notFound')) : `${pvMatchIdx + 1}/${pvTotal}`}
              </span>
              <button onClick={() => pvNav(-1)} disabled={pvTotal === 0}
                className="w-6 h-6 flex items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-[11px] disabled:opacity-40">▲</button>
              <button onClick={() => pvNav(1)} disabled={pvTotal === 0}
                className="w-6 h-6 flex items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-[11px] disabled:opacity-40">▼</button>
            </>
          )}
        </div>
        {pvSearch && pvTabHits.filter(h => h.idx !== detailTab).length > 0 && (
          <div className="flex items-center flex-wrap gap-1.5 mb-3 -mt-1">
            <span className="text-[11px] text-slate-400">{t('notesPlain.alsoFound')}</span>
            {pvTabHits.filter(h => h.idx !== detailTab).map(h => (
              <button key={h.idx} onClick={() => setDetailTab(h.idx)}
                className="text-[11px] font-semibold text-violet-600 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 max-w-[140px] truncate">
                {h.label}
              </button>
            ))}
          </div>
        )}
        {(() => {
          const dc = detailTab === 0 ? selected.content : (selected.tabs?.[detailTab - 1]?.content || '');
          return dc.trim() ? <MarkdownView source={dc} highlight={pvSearch || pendingHl || undefined} /> : <span className="text-slate-400 text-sm">{t('notesPlain.empty')}</span>;
        })()}
      </div>
    </div>
  ) : (
    <div className="h-full flex items-center justify-center text-center p-10 text-slate-400">
      <div>
        <NotebookText style={{ width: 40, height: 40 }} className="mx-auto mb-3 text-slate-200" />
        <p className="text-sm">{t('notesPlain.selectNoteLeftCreateNew')}</p>
      </div>
    </div>
  );

  const showPanelMobile = isMobile && (editing || selected || selectedMeta);

  return (
    // PENTING: root sengaja TIDAK memakai `content-animate`. Keyframes
    // `contentIn` memakai `transform` + fill-mode `both`, sehingga nilai
    // transform menempel permanen dan elemen ini menjadi containing block
    // untuk descendant `position: fixed` (panel mobile & modal) — akibatnya
    // `fixed inset-0` mengukur ke div ini, bukan ke viewport. Animasi
    // dipindah ke anak-anak yang BUKAN ancestor elemen fixed.
    <div className="p-4 sm:p-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[990] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white
          ${toast.type === 'error' ? 'bg-rose-500' : 'bg-emerald-500'}`}>
          {toast.type === 'error' ? <AlertTriangle style={{ width: 15, height: 15 }} /> : <CheckCircle2 style={{ width: 15, height: 15 }} />}
          {toast.msg}
        </div>
      )}
      {impFile && (
        <ImportNotesZipModal
          file={impFile}
          endpoint="/api/notes-plain"
          api={api}
          id={id}
          targetLabel="Notes Plain"
          onClose={() => setImpFile(null)}
          onDone={(r) => {
            load();
            if (!r.keepOpen) {
              setImpFile(null);
              showToast(id ? (r.ok + ' catatan diimpor.') : (r.ok + ' notes imported.'));
            }
          }} />
      )}
      {/* Header halaman */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 content-animate">
        <div>
          <h2 className="font-display font-bold text-slate-800 text-xl flex items-center gap-2">
            <NotebookText style={{ width: 22, height: 22 }} className="text-lime-600" />
            Notes Plain
          </h2>
          <p className="text-sm text-slate-400 mt-0.5 flex items-center gap-1.5">
            {t('notesPlain.notesDocumentImportNotRag')}
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-indigo-500"><Sparkles style={{ width: 11, height: 11 }} />{t('notesPlain.promoteRagNotesWhenReady')}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Btn onClick={onPickDoc} disabled={docBusy}>
            {docBusy ? <RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} /> : <FileUp style={{ width: 14, height: 14 }} />}
            {t('notesPlain.importDocument')}
          </Btn>
          <Btn onClick={() => zipRef.current && zipRef.current.click()}
            title={id ? 'Impor berkas .zip berisi .md menjadi catatan baru' : 'Import a .zip of .md files as new notes'}>
            <FileUp style={{ width: 14, height: 14 }} /> {id ? 'Impor ZIP' : 'Import ZIP'}
          </Btn>
          <Btn onClick={() => (selMode ? exitSelMode() : setSelMode(true))} tone={selMode ? 'primary' : 'default'}
            title={id ? 'Pilih beberapa catatan lalu unduh sebagai ZIP' : 'Select multiple notes and download as ZIP'}>
            <CheckSquare style={{ width: 14, height: 14 }} />
            {selMode ? (id ? 'Batal Pilih' : 'Cancel') : (id ? 'Pilih' : 'Select')}
          </Btn>
          {selMode && (
            <Btn onClick={downloadSelected} disabled={zipBusy || selIds.size === 0}>
              {zipBusy
                ? <RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} />
                : <Download style={{ width: 14, height: 14 }} />}
              {zipMsg
                ? (id ? 'Mengambil isi ' : 'Fetching ') + zipMsg
                : (id ? 'Unduh ZIP' : 'Download ZIP') + (selIds.size ? ' (' + selIds.size + ')' : '')}
            </Btn>
          )}
          <Btn tone="primary" onClick={startNew}><Plus style={{ width: 15, height: 15 }} /> {t('notesPlain.newNote')}</Btn>
        </div>
      </div>
      <input ref={fileRef} type="file" className="hidden"
        accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
        onChange={onDocSelected} />
      <input ref={zipRef} type="file" className="hidden" accept=".zip,application/zip,application/x-zip-compressed"
        onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) setImpFile(f); }} />
      <input ref={imgFileRef} type="file" className="hidden" accept="image/png,image/jpeg,image/webp,image/gif" multiple
        onChange={onImageSelected} />
      <div className="flex flex-col lg:flex-row gap-2 items-stretch">
        {/* Kiri: daftar — desktop: resizable + collapsible; mobile: TIDAK berubah (w-full biasa) */}
        <div className={`${showPanelMobile ? 'hidden' : 'content-animate'} lg:block shrink-0`}
          style={!isMobile ? { width: leftOpen ? leftW : 44, minWidth: leftOpen ? leftW : 44, transition: dragging ? 'none' : 'width 0.2s ease' } : undefined}>
          {!isMobile && !leftOpen ? (
            <button onClick={toggleLeft} title={t('notesPlain.openNotesList')}
              className="w-11 h-11 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-lime-600 hover:border-lime-300 transition-colors">
              <ChevronRight style={{ width: 16, height: 16 }} />
            </button>
          ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden w-full">
            <div className="p-3 border-b border-slate-100 space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search style={{ width: 14, height: 14 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('notesPlain.searchNotes')}
                    className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 outline-none focus:bg-white focus:border-blue-400" />
                  {srvBusy && (
                    <RefreshCw style={{ width: 13, height: 13 }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
                  )}
                </div>
                {!isMobile && (
                  <button onClick={toggleLeft} title={t('notesPlain.collapse')}
                    className="hidden lg:flex w-8 h-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300">
                    <ChevronLeft style={{ width: 15, height: 15 }} />
                  </button>
                )}
              </div>
              <div className="flex gap-1.5">
                {[['all', t('notesPlain.all')], ['private', t('notesPlain.private')], ['common', t('notesPlain.shared')]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setScopeF(k)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors
                      ${scopeF === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            {selMode && (
              <div className="px-3 py-2 border-b border-slate-100 bg-lime-50/60 flex items-center gap-2">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll}
                    className="w-4 h-4 accent-lime-600" />
                  {(id ? 'Pilih semua' : 'Select all') + ' (' + visibleIds.length + ')'}
                </label>
                <span className="ml-auto text-xs text-slate-500">
                  {selIds.size + (id ? ' dipilih' : ' selected')}
                </span>
              </div>
            )}
            <div className="max-h-[62vh] lg:max-h-[calc(100vh-300px)] overflow-y-auto divide-y divide-slate-50">
              {loading ? (
                <div className="px-4 py-12 text-center text-slate-400 text-sm">
                  <RefreshCw style={{ width: 18, height: 18 }} className="animate-spin mx-auto mb-2" /> {t('notesPlain.loading')}
                </div>
              ) : (ql && !srv && srvBusy) ? (
                <div className="px-4 py-12 text-center text-slate-400 text-sm">
                  <RefreshCw style={{ width: 18, height: 18 }} className="animate-spin mx-auto mb-2" />
                  {id ? 'Mencari…' : 'Searching…'}
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-4 py-12 text-center text-slate-400 text-sm">{t('notesPlain.noNotes')}</div>
              ) : filtered.map(({ n, m }) => {
                const renderSnippet = (txt) => {
                  if (!txt) return null;
                  const t0 = terms[0];
                  const idx = txt.toLowerCase().indexOf(t0);
                  if (idx === -1) return txt;
                  return (<>
                    {txt.slice(0, idx)}
                    <mark className="bg-yellow-200 rounded-sm px-0.5">{txt.slice(idx, idx + t0.length)}</mark>
                    {txt.slice(idx + t0.length)}
                  </>);
                };
                return (
                  <div key={n.id} className="flex items-stretch">
                  {selMode && (
                    <label className="flex items-center pl-3 shrink-0 cursor-pointer">
                      <input type="checkbox" checked={selIds.has(n.id)} onChange={() => toggleSel(n.id)}
                        className="w-4 h-4 accent-lime-600" />
                    </label>
                  )}
                  <button onClick={() => (selMode ? toggleSel(n.id) : openSearchResult(n, m))}
                    className={`flex-1 min-w-0 text-left px-4 py-3 min-h-[56px] transition-colors
                      ${selectedId === n.id && !isMobile ? 'bg-lime-50/70 border-l-4 border-l-lime-500' : 'hover:bg-slate-50 active:bg-slate-100'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-700 text-sm truncate">{n.title || (t('notesPlain.untitled'))}</span>
                      <ScopeBadge scope={n.scope} id={id} />
                    </div>
                    {ql && m.snippet ? (
                      <p className="mt-1 text-xs text-slate-600 leading-relaxed line-clamp-2 break-words">
                        {m.tabIdx > 0 && (
                          <span className="text-[10px] font-bold text-violet-600 bg-violet-50 border border-violet-200 rounded px-1 mr-1.5">
                            {m.tabTitle || `Tab ${m.tabIdx}`}
                          </span>
                        )}
                        {renderSnippet(m.snippet)}
                      </p>
                    ) : (
                      <>
                        {n.contentPreview && (
                          <p className="mt-1 text-xs text-slate-500 line-clamp-1 break-words">{n.contentPreview}</p>
                        )}
                        <p className="mt-0.5 text-xs text-slate-400 truncate flex items-center gap-1.5">
                          <span className="truncate">
                            {fmtDt(n.updatedAt, locale)}{n.updatedBy ? ` · ${n.updatedBy}` : ''}
                          </span>
                          {n.tabCount > 0 && (
                            <span className="shrink-0 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1">
                              {n.tabCount} tab
                            </span>
                          )}
                          {(n.sizeBytes || 0) >= NP_WARN_BYTES && (
                            <span title={id ? 'Catatan besar — lambat dimuat' : 'Large note — slow to load'}
                              className="shrink-0 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">
                              {humanBytes(n.sizeBytes)}
                            </span>
                          )}
                        </p>
                      </>
                    )}
                  </button>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>

        {/* Divider drag-resize (desktop, saat panel kiri terbuka) */}
        {!isMobile && leftOpen && (
          <div onMouseDown={startDrag} onDoubleClick={() => { setLeftW(320); try { localStorage.setItem('notesPlainLeftW', '320'); } catch (_) {} }}
            className="hidden lg:flex self-stretch group"
            title={t('notesPlain.dragResizeDoubleClickReset')}
            style={{ width: 10, cursor: 'col-resize', flexShrink: 0, justifyContent: 'center', minHeight: 120 }}>
            <div style={{ width: 3, borderRadius: 3, background: dragging ? '#84cc16' : '#e2e8f0', transition: 'background 0.15s' }}
              className="group-hover:!bg-lime-300" />
          </div>
        )}

        {/* Kanan: panel viewer/editor */}
        {isMobile ? (
          showPanelMobile && (
            <div className="fixed inset-0 z-[900] bg-white flex flex-col"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
              {panel}
            </div>
          )
        ) : (
          <div className="flex-1 min-w-0 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden min-h-[60vh] content-animate">
            {panel}
          </div>
        )}
      </div>
      {/* Modals */}
      {docResult && (
        <DocReviewModal
          id={id} result={docResult}
          onClose={() => setDocResult(null)}
          onInsertText={(md) => {
            const imgs = docResult?.images || [];
            const used = new Set();
            let out = String(md).replace(/\[\[IMG_(\d+)\]\]/g, (_m, n) => {
              const ix = parseInt(n, 10) - 1;
              if (ix >= 0 && ix < imgs.length) { used.add(ix); return `\n\n<img src="${imgs[ix].data_uri}" style="max-width:100%" />\n\n`; }
              return '';
            });
            const leftover = imgs.filter((_im, ix) => !used.has(ix));
            if (leftover.length) out += '\n\n' + leftover.map(im => `<img src="${im.data_uri}" style="max-width:100%" />`).join('\n\n') + '\n\n';
            insertAtCursor(out);
            setDocResult(null);
            showToast(t('notesPlain.markdownImagesInserted'));
          }}
          onInsertImage={(uri) => insertAtCursor(`\n\n<img src="${uri}" style="max-width:100%" />\n\n`)}
        />
      )}
      {promote && (
        <PromoteModal id={id} note={promote.note} mode={promote.mode} busy={promoteBusy}
          onConfirm={doPromote} onClose={() => setPromote(null)} />
      )}
    </div>
  );
}
